/**
 * Companion Watch Apps — strength phase progress helpers (P2b-2).
 *
 * Extracted VERBATIM (behavior-preserving) from
 * web/src/components/UnifiedWorkoutTimer.tsx:115-142 ("Bug #76", Feb 2026) so
 * the P2c phone refactor can consume them instead of owning the logic, and so
 * the watch can reference one source of truth.
 *
 * This is the PHONE strength model: NOT wall-clock — a strength phase's duration
 * is split into equal per-exercise slices and the live phase time is clamped to
 * the current exercise's slice, so the progress bar advances per-exercise.
 *
 * ⚠️ The WATCH strength UX is different: SET-BASED (set-completion advancement +
 * crown-logged reps/weight + rest timers), specified in
 * specs/WATCH_COMPANION_UX.md. That set-runner is the engine's real strength
 * model for the watch and will be built once that spec is in the repo (it is
 * referenced as canonical but not yet committed to origin/main). These helpers
 * are the faithful phone extraction; they do NOT define the watch set-runner.
 */

/** Time (seconds) allocated to each exercise within a phase. */
export function getTimePerExercise(phaseDurationSeconds: number, exerciseCount: number): number {
  if (exerciseCount === 0) return 0;
  return phaseDurationSeconds / exerciseCount;
}

/** The phase-time boundary (seconds) at the start of a given exercise index. */
export function getExerciseBoundary(exerciseIndex: number, timePerExercise: number): number {
  return exerciseIndex * timePerExercise;
}

/** Exercise-clamped phase progress percentage (0..100). */
export function getExerciseBasedPhaseProgress(
  phaseTime: number,
  currentExerciseIndex: number,
  totalExercises: number,
  phaseDurationSeconds: number,
): number {
  if (totalExercises === 0 || phaseDurationSeconds === 0) return 0;
  const timePerExercise = getTimePerExercise(phaseDurationSeconds, totalExercises);
  const previousBoundary = getExerciseBoundary(currentExerciseIndex, timePerExercise);
  const currentBoundary = getExerciseBoundary(currentExerciseIndex + 1, timePerExercise);
  // Clamp phase time within the current exercise's slice.
  const clampedPhaseTime = Math.max(previousBoundary, Math.min(currentBoundary, phaseTime));
  return (clampedPhaseTime / phaseDurationSeconds) * 100;
}
